import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Sparkles, FileText, Scale } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

interface ServiceAgreementSettingsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  bidId?: string;
  businessId?: string;
  clientName?: string;
  monthlyPrice?: number;
}

export function ServiceAgreementSettingsSheet({
  isOpen,
  onClose,
  bidId,
  businessId,
  clientName,
  monthlyPrice,
}: ServiceAgreementSettingsSheetProps) {
  const navigate = useNavigate();
  const { user } = useUser();
  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const hasPurchasedCredits = Boolean((subscription as any)?.purchasedCredits && (subscription as any).purchasedCredits > 0);
  const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);
  const isCreditBlocked = subscription !== undefined && !hasPurchasedCredits && !hasActivePaidPlan;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // State for settings
  const [agreementType, setAgreementType] = useState("standard");
  const [termLength, setTermLength] = useState("12 months");
  const [customInstructions, setCustomInstructions] = useState("");

  // Included Sections State (aligned with actual service agreement template)
  // Note: Scope of Services, Compensation, Term & Termination, and Signatures are always required
  const [includedSections, setIncludedSections] = useState({
    insurance: true, // Risk, Liability & Insurance section
    indemnification: true, // Indemnification clause
    confidentiality: true, // Confidentiality clause
    nonSolicitation: true, // Non-Solicitation clause
    qualityAssurance: true, // Quality Assurance procedures
    exhibitA: true, // Exhibit A: Detailed Scope of Work
    forceMajeure: true, // Force Majeure clause
  });

  const toggleSection = (key: keyof typeof includedSections) => {
    setIncludedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGenerate = () => {
    if (isCreditBlocked) {
      setShowUpgradeModal(true);
      return;
    }

    // Construct the prompt based on settings
    const sectionsList = Object.entries(includedSections)
      .filter(([_, included]) => included)
      .map(([key]) => key.replace(/([A-Z])/g, " $1").trim()) // CamelCase to Title Case
      .join(", ");

    const prompt = `Create a ${agreementType} Service Agreement for ${clientName || "the client"}.
    
Agreement Type: ${agreementType}
Term Length: ${termLength}
${monthlyPrice !== undefined ? `Monthly Price: $${monthlyPrice.toFixed(2)}\n` : ""}Sections to Include: ${sectionsList}.

${customInstructions ? `Additional Instructions: ${customInstructions}` : ""}`;

    // Navigate to Document Writer with state
    navigate("/document-writer", {
      state: {
        generateFromPrompt: true,
        prompt,
        templateType: "service-agreement",
        relatedBidId: bidId,
        relatedClientId: businessId,
      },
    });

    onClose();
  };

  return (
    <>
      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Access your AI Business Assistant to generate comprehensive, professional service agreements instantly."
        feature="AI Service Agreement Generation"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="flex h-full w-[400px] flex-col sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-purple-600" />
            Service Agreement Settings
          </SheetTitle>
          <SheetDescription>
            Configure how your service agreement should be generated.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mr-4 mt-6 flex-1 pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Template Type & Term */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Select value={agreementType} disabled>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Term Length</Label>
                <Select value={termLength} onValueChange={setTermLength}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6 months">6 Months</SelectItem>
                    <SelectItem value="12 months">12 Months (Standard)</SelectItem>
                    <SelectItem value="24 months">24 Months</SelectItem>
                    <SelectItem value="month-to-month">Month-to-Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Optional Sections */}
            <div className="space-y-3">
              <Label>Optional Sections</Label>
              <p className="text-xs text-muted-foreground">
                Scope of Services, Compensation, Term & Termination, and Signatures are always
                included.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="insurance"
                    checked={includedSections.insurance}
                    onCheckedChange={() => toggleSection("insurance")}
                  />
                  <Label htmlFor="insurance">Insurance Requirements</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="indemnification"
                    checked={includedSections.indemnification}
                    onCheckedChange={() => toggleSection("indemnification")}
                  />
                  <Label htmlFor="indemnification">Indemnification</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="confidentiality"
                    checked={includedSections.confidentiality}
                    onCheckedChange={() => toggleSection("confidentiality")}
                  />
                  <Label htmlFor="confidentiality">Confidentiality</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nonSolicitation"
                    checked={includedSections.nonSolicitation}
                    onCheckedChange={() => toggleSection("nonSolicitation")}
                  />
                  <Label htmlFor="nonSolicitation">Non-Solicitation</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="qualityAssurance"
                    checked={includedSections.qualityAssurance}
                    onCheckedChange={() => toggleSection("qualityAssurance")}
                  />
                  <Label htmlFor="qualityAssurance">Quality Assurance</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="exhibitA"
                    checked={includedSections.exhibitA}
                    onCheckedChange={() => toggleSection("exhibitA")}
                  />
                  <Label htmlFor="exhibitA">Exhibit A (Detailed SOW)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="forceMajeure"
                    checked={includedSections.forceMajeure}
                    onCheckedChange={() => toggleSection("forceMajeure")}
                  />
                  <Label htmlFor="forceMajeure">Force Majeure</Label>
                </div>
              </div>
            </div>

            {/* Custom Instructions */}
            <div className="space-y-2">
              <Label>Custom Instructions</Label>
              <Textarea
                placeholder="Any specific clauses or details to include? (e.g., 'Include a Net 30 payment term')"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                className="h-24"
              />
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-auto border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} className="gap-2 bg-purple-600 hover:bg-purple-700">
            <Sparkles className="h-4 w-4" />
            Generate Agreement
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    </>
  );
}
